/**
 * Description: DOM renderer for pet container + SVG / multi-pose; uses host and injectable fetch only.
 * Usage: new Renderer(host, fetchImpl); init(config, mountParent); update(x,y,state,dir); destroy().
 */
export class Renderer {
  constructor(host, fetchImpl) {
    this._host = host;
    this._fetch = fetchImpl;
    this._container = null;
    this._petEl = null;
    this._currentState = '';
    this._currentDir = 1;
    this._multiPose = false;
    this._poses = Object.create(null);
    this._activePose = '';
    this._statePoseMap = Object.create(null);
  }

  init(config, mountParent) {
    const doc = this._host.document;
    const parent = mountParent || doc.body;

    this._container = doc.createElement('div');
    this._container.id = 'ndp-pet-container';
    this._container.setAttribute('aria-hidden', 'true');
    this._container.setAttribute('role', 'presentation');

    this._petEl = doc.createElement('div');
    this._petEl.id = 'ndp-pet';
    this._petEl.className = 'ndp-pet ndp-state-idle';
    this._currentState = 'idle';

    this._container.appendChild(this._petEl);
    parent.appendChild(this._container);

    if (config.poses && config.statePoses) {
      return this._initMultiPose(config);
    }
    return this._initSingleSvg(config);
  }

  _initMultiPose(config) {
    this._multiPose = true;
    this._statePoseMap = config.statePoses;
    this._poses = Object.create(null);
    this._activePose = '';

    const poseIds = Object.keys(config.poses);
    const promises = [];

    for (let i = 0; i < poseIds.length; i++) {
      const poseId = poseIds[i];
      const url = config.characterBaseUrl + config.poses[poseId];
      promises.push(
        this._fetch(url)
          .then((res) => {
            if (!res.ok) throw new Error('Pose SVG missing: ' + poseId);
            return res.text();
          })
          .then((svg) => {
            const doc = this._host.document;
            const wrapper = doc.createElement('div');
            wrapper.className = 'ndp-pose ndp-pose-' + poseId;
            wrapper.style.display = 'none';
            wrapper.innerHTML = svg;
            const svgNode = wrapper.querySelector('svg');
            if (svgNode) {
              svgNode.setAttribute('width', config.size || 48);
              svgNode.setAttribute('height', config.size || 48);
            }
            this._petEl.appendChild(wrapper);
            this._poses[poseId] = wrapper;
          })
          .catch(() => {
            this._poses[poseId] = null;
          })
      );
    }

    return Promise.all(promises).then(() => {
      const defaultPose = this._statePoseMap.idle || poseIds[0];
      this._showPose(defaultPose);
      this._applyColors(config);
    });
  }

  _initSingleSvg(config) {
    this._multiPose = false;
    const svgUrl = config.characterBaseUrl + config.character + '.svg';
    return this._fetch(svgUrl)
      .then((res) => {
        if (!res.ok) throw new Error('SVG not found');
        return res.text();
      })
      .then((svg) => {
        this._petEl.innerHTML = svg;
        const svgNode = this._petEl.querySelector('svg');
        if (svgNode) {
          svgNode.setAttribute('width', config.size || 48);
          svgNode.setAttribute('height', config.size || 48);
        }
        this._applyColors(config);
      })
      .catch(() => {
        this._petEl.textContent = '\uD83D\uDC3E';
        this._petEl.style.fontSize = (config.size || 48) + 'px';
        this._petEl.style.lineHeight = '1';
      });
  }

  _showPose(poseId) {
    if (poseId === this._activePose) return;
    if (this._poses[this._activePose]) this._poses[this._activePose].style.display = 'none';
    if (this._poses[poseId]) {
      this._poses[poseId].style.display = '';
      this._activePose = poseId;
    }
  }

  update(x, y, state, dir) {
    if (!this._container) return;

    this._container.style.transform = 'translate3d(' + Math.round(x) + 'px,' + Math.round(y) + 'px,0)';

    if (state !== this._currentState) {
      this._petEl.classList.remove('ndp-state-' + this._currentState);
      this._petEl.classList.add('ndp-state-' + state);
      this._currentState = state;

      if (this._multiPose && this._statePoseMap[state]) {
        this._showPose(this._statePoseMap[state]);
      }
    }

    if (dir !== this._currentDir) {
      this._petEl.style.transform = dir < 0 ? 'scaleX(-1)' : 'scaleX(1)';
      this._currentDir = dir;
    }
  }

  _applyColors(config) {
    if (!this._petEl || !config.customColors) return;
    const colors = config.customColors;
    for (const key in colors) {
      if (Object.prototype.hasOwnProperty.call(colors, key) && colors[key]) {
        this._petEl.style.setProperty('--ndp-color-' + key, colors[key]);
      }
    }
  }

  setOpacity(value) {
    if (!this._container) return;
    this._container.style.opacity = value;
  }

  destroy() {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
    this._container = null;
    this._petEl = null;
    this._poses = Object.create(null);
    this._activePose = '';
    this._multiPose = false;
  }

  getElement() {
    return this._container;
  }
}
